import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Robot, FloppyDisk, FilePdf, FileDoc,
  BookBookmark, MagnifyingGlass, Check, ArrowClockwise,
  CheckCircle, Circle, Spinner, Rows
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import CCTPAIToolbar from './CCTPAIToolbar';
import usePermissions from '../../hooks/usePermissions';
import useLotsConfies, { peutEcrireLot } from '../../hooks/useLotsConfies';
import LotConfieBar from './LotConfieBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audit 2026-09 CON-03 — aligne la structure CCTP sur la DPGF sans rien effacer :
// lots/postes manquants ajoutés vides, textes existants conservés, entrées CCTP
// orphelines (supprimées de la DPGF) gardées en fin de liste.
// Même règle que backend/services/cctp_service.py::merge_cctp_with_dpgf.
export function alignCctpOnDpgf(cctpLots, dpgfLots) {
  const byLot = new Map();
  (cctpLots || []).forEach(l => { if (l && l.lot_id != null && !byLot.has(l.lot_id)) byLot.set(l.lot_id, l); });
  const seenLots = new Set();
  const out = [];
  (dpgfLots || []).forEach(dl => {
    if (!dl || dl.id == null || seenLots.has(dl.id)) return;
    seenLots.add(dl.id);
    const old = byLot.get(dl.id);
    const oldPostes = new Map();
    (old?.postes || []).forEach(p => { if (p && p.poste_id != null && !oldPostes.has(p.poste_id)) oldPostes.set(p.poste_id, p); });
    const seenPostes = new Set();
    const postes = [];
    (dl.postes || []).forEach(dp => {
      if (!dp || dp.id == null || seenPostes.has(dp.id)) return;
      seenPostes.add(dp.id);
      postes.push({ poste_id: dp.id, descriptif: oldPostes.get(dp.id)?.descriptif || '' });
    });
    oldPostes.forEach((p, pid) => { if (!seenPostes.has(pid)) postes.push({ poste_id: pid, descriptif: p.descriptif || '' }); });
    out.push({ lot_id: dl.id, generalites: old?.generalites || '', postes });
  });
  byLot.forEach((l, lid) => {
    if (seenLots.has(lid)) return;
    out.push({
      lot_id: lid,
      generalites: l.generalites || '',
      postes: (l.postes || []).filter(p => p && p.poste_id != null).map(p => ({ poste_id: p.poste_id, descriptif: p.descriptif || '' })),
    });
  });
  return out;
}

function PosteEditor({ poste, dpgfPoste, lotNom, chantierId, canEdit, canIA = true, onUpdate, onSaveBiblio }) {
  const [generating, setGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState(null);
  const [biblioOpen, setBiblioOpen] = useState(false);
  const [biblioSearch, setBiblioSearch] = useState('');
  const [biblioResults, setBiblioResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Sprint D #9 — Mode édition (Textarea brut) vs Aperçu (rendu Markdown)
  const [previewMode, setPreviewMode] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/cctp/generate-ai`, {
        poste_designation: dpgfPoste?.designation || '',
        quantite: dpgfPoste?.quantite || 0,
        unite: dpgfPoste?.unite || '',
        lot_nom: lotNom,
        // Audit 2026-09 CON-16 — type et description réels lus côté serveur sur le chantier
      }, { withCredentials: true });
      setGeneratedText(data.descriptif);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de génération IA');
    } finally {
      setGenerating(false);
    }
  };

  const acceptGenerated = () => {
    onUpdate(generatedText);
    setGeneratedText(null);
    toast.success('Descriptif accepté');
  };

  const handleBiblioSearch = async () => {
    setSearchLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/bibliotheque/search?q=${encodeURIComponent(biblioSearch)}&lot=${encodeURIComponent(lotNom)}`, { withCredentials: true });
      setBiblioResults(data);
    } catch (err) { console.error(err); }
    finally { setSearchLoading(false); }
  };

  return (
    <div className="border border-[#E0E0DB] mb-4" data-testid={`cctp-poste-${poste.poste_id}`}>
      {/* DPGF Reference */}
      {dpgfPoste && (
        <div className="px-4 py-2 bg-[#F5F5F3] border-b border-[#E0E0DB] text-xs text-[#6B6B6B] flex items-center gap-4">
          <span className="font-medium text-[#2C2C2C]">{dpgfPoste.designation}</span>
          <span>{dpgfPoste.quantite} {dpgfPoste.unite}</span>
          <span>{dpgfPoste.prix_unitaire?.toLocaleString('fr-FR')} € HT/u</span>
        </div>
      )}
      
      {/* Descriptif editor */}
      <div className="p-4">
        {generatedText !== null ? (
          <div>
            <p className="text-xs font-bold text-[#6A1B9A] mb-2 uppercase tracking-wider">Proposition IA</p>
            <div className="p-3 bg-[#F3E5F5] border border-[#CE93D8] text-sm mb-3 max-h-64 overflow-y-auto cctp-preview" data-testid="cctp-generated-preview">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => <h3 className="text-[#1E3A5F] font-bold text-base mt-2 mb-1" {...props} />,
                  h2: ({ node, ...props }) => <h4 className="text-[#1E3A5F] font-bold text-sm uppercase tracking-wide mt-2 mb-1" {...props} />,
                  h3: ({ node, ...props }) => <h5 className="font-bold text-sm mt-1 mb-0.5" {...props} />,
                  strong: ({ node, ...props }) => <strong className="text-[#1E3A5F] font-bold" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1 my-1.5" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1 my-1.5" {...props} />,
                  p: ({ node, ...props }) => <p className="my-1 leading-relaxed" {...props} />,
                }}
              >
                {generatedText || ''}
              </ReactMarkdown>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-[#2D6A4F] hover:bg-[#3B8B6B] rounded-none gap-1 text-xs" onClick={acceptGenerated}>
                <Check size={12} /> Accepter
              </Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs" onClick={() => setGeneratedText(null)}>
                Modifier d'abord
              </Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs text-[#6A1B9A] border-[#6A1B9A]" onClick={handleGenerate}>
                <ArrowClockwise size={12} /> Regénérer
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Sprint D #9 — Toggle Édition / Aperçu */}
            <div className="flex items-center gap-1 mb-2 border-b border-[#E0E0DB]">
              <button
                onClick={() => setPreviewMode(false)}
                className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border-b-2 transition-colors ${!previewMode ? 'border-[#C75B39] text-[#C75B39] font-bold' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
                data-testid={`cctp-edit-mode-${poste.poste_id}`}
              >
                Édition
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border-b-2 transition-colors ${previewMode ? 'border-[#C75B39] text-[#C75B39] font-bold' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
                data-testid={`cctp-preview-mode-${poste.poste_id}`}
              >
                Aperçu
              </button>
              <span className="ml-auto text-[10px] text-[#9A9A9A] italic px-2">Mise en forme : ## en début de ligne pour un titre, **texte** pour du gras</span>
            </div>
            {previewMode ? (
              <div
                className="cctp-preview border border-[#E0E0DB] bg-[#FAFAF8] px-4 py-3 min-h-[8rem] text-sm text-[#2C2C2C]"
                data-testid={`cctp-preview-content-${poste.poste_id}`}
              >
                {poste.descriptif ? (
                  <ReactMarkdown
                    components={{
                      h1: ({ node, ...props }) => <h3 className="text-[#1E3A5F] font-bold text-base mt-3 mb-2" {...props} />,
                      h2: ({ node, ...props }) => <h4 className="text-[#1E3A5F] font-bold text-sm uppercase tracking-wide mt-3 mb-1" {...props} />,
                      h3: ({ node, ...props }) => <h5 className="text-[#2C2C2C] font-bold text-sm mt-2 mb-1" {...props} />,
                      strong: ({ node, ...props }) => <strong className="text-[#1E3A5F] font-bold" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1 my-2" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1 my-2" {...props} />,
                      li: ({ node, ...props }) => <li className="text-sm" {...props} />,
                      p: ({ node, ...props }) => <p className="my-2 leading-relaxed" {...props} />,
                    }}
                  >
                    {poste.descriptif}
                  </ReactMarkdown>
                ) : (
                  <p className="text-[#9A9A9A] italic">(Aucun descriptif — passer en mode Édition pour ajouter du contenu)</p>
                )}
              </div>
            ) : (
              <Textarea
                value={poste.descriptif || ''}
                onChange={e => onUpdate(e.target.value)}
                readOnly={!canEdit}
                data-testid={`cctp-poste-textarea-${poste.poste_id}`}
                rows={poste.descriptif ? Math.min(Math.max(poste.descriptif.split('\n').length + 2, 4), 16) : 4}
                className="rounded-none text-sm border-[#E0E0DB] focus:border-[#C75B39] font-mono"
                placeholder="Descriptif technique CCTP du poste…"
              />
            )}
            {canEdit && <div className="flex items-center gap-2 mt-2">
              {canIA && <CCTPAIToolbar
                chantierId={chantierId}
                lotName={lotNom}
                posteDesignation={dpgfPoste?.designation || ''}
                currentText={poste.descriptif || ''}
                onAccept={(text) => onUpdate(text)}
              />}
              {canIA && <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs text-[#6A1B9A] border-[#6A1B9A]" onClick={handleGenerate} disabled={generating} data-testid={`ai-generate-${poste.poste_id}`}>
                {generating ? <Spinner size={12} className="animate-spin" /> : <Robot size={12} />}
                {generating ? 'Génération...' : 'Rédiger avec l\'IA'}
              </Button>}
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs" onClick={() => onSaveBiblio(dpgfPoste?.designation || '', poste.descriptif, lotNom)} disabled={!poste.descriptif} data-testid={`save-biblio-${poste.poste_id}`}>
                <BookBookmark size={12} /> Bibliothèque
              </Button>
              <Dialog open={biblioOpen} onOpenChange={setBiblioOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs" data-testid={`search-biblio-${poste.poste_id}`}>
                    <MagnifyingGlass size={12} /> Rechercher
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Bibliothèque cabinet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input value={biblioSearch} onChange={e => setBiblioSearch(e.target.value)} placeholder="Rechercher..." className="rounded-none" onKeyDown={e => e.key === 'Enter' && handleBiblioSearch()} />
                      <Button onClick={handleBiblioSearch} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" disabled={searchLoading}>Chercher</Button>
                    </div>
                    {searchLoading ? <Skeleton className="h-24" /> : biblioResults.length === 0 ? (
                      <p className="text-sm text-[#6B6B6B] text-center py-8">Aucun résultat dans la bibliothèque</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {biblioResults.map(r => (
                          <div key={r.id} className="border border-[#E0E0DB] p-3 hover:border-[#C75B39] cursor-pointer transition-colors" onClick={() => { onUpdate(r.descriptif_cctp); setBiblioOpen(false); toast.success('Descriptif appliqué'); }}>
                            <p className="text-sm font-medium text-[#2C2C2C]">{r.intitule_poste}</p>
                            <p className="text-xs text-[#6B6B6B] mt-1 line-clamp-2">{r.descriptif_cctp?.slice(0, 120)}...</p>
                            <p className="text-[10px] text-[#9A9A9A] mt-1">Lot: {r.lot} — Utilisé {r.utilise_nb_fois}x</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>}
          </>
        )}
      </div>
    </div>
  );
}


export default function CCTPEditor({ chantierId, chantierName, onBack, onNavigateDPGF }) {
  const { perms } = usePermissions(chantierId);
  // Iter63c-A — CCTP strict archi + override can_modify_cctp
  // RP44-45 / BET-A4 — délégation par lot : `canEdit` = tout le CCTP (génération
  // complète) ; `canEditLot(id)` = un lot (lots confiés, ou tous sauf ceux confiés à
  // d'autres). Enregistrement LOT PAR LOT (PUT /cctp/lots/{lot_id}) : plus de
  // réécriture du document entier, deux personnes n'effacent plus le travail de l'autre.
  const lotsConfies = useLotsConfies(chantierId);
  const porteeCctp = lotsConfies.data.moi.cctp;
  const canEdit = !!perms?.can_modify_cctp && !!porteeCctp.complete;
  const canEditLot = (lotId) => peutEcrireLot(porteeCctp, lotId);
  const refreshLotsRef = useRef(null);
  refreshLotsRef.current = lotsConfies.refresh;
  const latestRef = useRef([]);
  const dirtyLots = useRef(new Set());
  const editTitle = canEdit ? undefined : 'Lecture seule : modification du CCTP réservée à l\'architecte (ou droit confié). Demandez à l\'architecte.';
  const [dpgfLots, setDpgfLots] = useState([]);
  const [cctpData, setCctpData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLotId, setActiveLotId] = useState(null);
  const [bulkGen, setBulkGen] = useState(null); // {lotIdx, lotTotal, lotNom, posteIdx, posteTotal}
  const saveTimeout = useRef(null);

  useEffect(() => { fetchData(); }, [chantierId]);

  const fetchData = async () => {
    try {
      const [dpgfRes, cctpRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/cctp`, { withCredentials: true }),
      ]);
      const dpgf = dpgfRes.data.lots || [];
      setDpgfLots(dpgf);
      setCctpData(cctpRes.data.lots || []);
      if (dpgf.length > 0) setActiveLotId(dpgf[0].id);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Impossible de charger le CCTP');
    }
    finally { setLoading(false); }
  };

  const autoSave = useCallback(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const lotIds = [...dirtyLots.current];
      dirtyLots.current.clear();
      let aRelire = false;
      for (const lotId of lotIds) {
        const lot = latestRef.current.find(l => l.lot_id === lotId);
        if (!lot) continue;
        try {
          const { data } = await axios.put(
            `${API_URL}/api/chantiers/${chantierId}/cctp/lots/${encodeURIComponent(lotId)}`,
            { generalites: lot.generalites || '', postes: lot.postes || [] },
            { withCredentials: true },
          );
          if (data?.a_relire?.length) aRelire = true;
        } catch (err) {
          // Audit 2026-09 CON-05 — plus d'échec silencieux : l'utilisateur sait que sa saisie n'est pas enregistrée
          console.error('Autosave error:', err);
          dirtyLots.current.add(lotId);
          toast.error(`CCTP non enregistré : ${err.response?.data?.detail || 'erreur réseau'}`, { id: 'cctp-autosave-error' });
        }
      }
      if (aRelire) refreshLotsRef.current?.(); // RP45 — lot confié « À relire »
    }, 800);
  }, [chantierId]);

  const updateCCTP = (newData, lotId) => {
    if (!canEditLot(lotId)) return;
    setCctpData(newData);
    latestRef.current = newData;
    dirtyLots.current.add(lotId);
    autoSave();
  };

  // Audit 2026-09 CON-03 — la saisie crée le lot / poste CCTP s'il n'existe pas encore
  // (plus besoin de « Générer le CCTP » au préalable).
  const updateGeneralites = (lotId, text) => {
    const aligned = alignCctpOnDpgf(cctpData, dpgfLots);
    const newData = aligned.map(l => l.lot_id === lotId ? { ...l, generalites: text } : l);
    updateCCTP(newData, lotId);
  };

  const updatePosteDescriptif = (lotId, posteId, text) => {
    const aligned = alignCctpOnDpgf(cctpData, dpgfLots);
    const newData = aligned.map(l => {
      if (l.lot_id !== lotId) return l;
      const postes = l.postes.some(p => p.poste_id === posteId)
        ? l.postes.map(p => p.poste_id === posteId ? { ...p, descriptif: text } : p)
        : [...l.postes, { poste_id: posteId, descriptif: text }];
      return { ...l, postes };
    });
    updateCCTP(newData, lotId);
  };

  const saveToBibliotheque = async (intitule, descriptif, lot) => {
    if (!descriptif) return;
    try {
      await axios.post(`${API_URL}/api/bibliotheque`, {
        lot, intitule_poste: intitule, descriptif_cctp: descriptif, tags: [], type_projet: 'renovation'
      }, { withCredentials: true });
      toast.success('Enregistré en bibliothèque');
    } catch (err) { toast.error('Erreur'); }
  };

  const generateAllCCTP = async () => {
    const totalLots = dpgfLots.length;
    if (totalLots === 0) { toast.error('Aucun lot DPGF'); return; }
    const confirmMsg = `Générer les descriptifs CCTP pour TOUS les lots (${totalLots}) ? Cette opération peut prendre plusieurs minutes et consomme des crédits IA.`;
    if (!window.confirm(confirmMsg)) return;

    // Audit 2026-09 CON-03 — chaque lot ET chaque poste DPGF a son entrée CCTP
    // (y compris les postes ajoutés à un lot CCTP déjà existant).
    let working = alignCctpOnDpgf(cctpData, dpgfLots);

    let generated = 0, skipped = 0, errors = 0;
    // A6 (P2#18) — cause du premier échec, annoncée en rouge ; service indisponible → on arrête
    let causeEchec = '';
    let arret = false;
    for (let i = 0; i < totalLots && !arret; i++) {
      const lot = dpgfLots[i];
      const postes = lot.postes || [];
      setBulkGen({ lotIdx: i + 1, lotTotal: totalLots, lotNom: lot.nom, posteIdx: 0, posteTotal: postes.length });
      for (let j = 0; j < postes.length; j++) {
        const dpgfPoste = postes[j];
        setBulkGen(prev => prev ? { ...prev, posteIdx: j + 1 } : prev);
        // Skip already-filled postes
        const cctpLot = working.find(l => l.lot_id === lot.id);
        const existing = cctpLot?.postes?.find(p => p.poste_id === dpgfPoste.id);
        if (existing?.descriptif && existing.descriptif.length > 20) { skipped++; continue; }
        try {
          const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/cctp/generate-ai`, {
            poste_designation: dpgfPoste.designation || '',
            quantite: dpgfPoste.quantite || 0,
            unite: dpgfPoste.unite || '',
            lot_nom: lot.nom,
          }, { withCredentials: true });
          working = working.map(l => l.lot_id !== lot.id ? l : {
            ...l,
            postes: l.postes.map(p => p.poste_id === dpgfPoste.id ? { ...p, descriptif: data.descriptif } : p),
          });
          setCctpData([...working]);
          generated++;
        } catch (err) {
          errors++;
          if (!causeEchec) causeEchec = err.response?.data?.detail || (err.response ? `erreur ${err.response.status}` : 'réseau indisponible');
          if ([401, 403, 503].includes(err.response?.status)) { arret = true; break; }
          console.error('CCTP gen err', lot.nom, dpgfPoste.designation, err);
        }
      }
    }
    // Persist final state
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/cctp`, { lots: working }, { withCredentials: true });
    } catch (err) {
      setBulkGen(null);
      toast.error(`CCTP généré mais non enregistré : ${err.response?.data?.detail || 'erreur réseau'}`, { duration: 10000 });
      return;
    }
    setBulkGen(null);
    if (errors > 0 && generated === 0) {
      toast.error('Génération des CCTP impossible', { description: causeEchec, duration: 12000 });
    } else if (errors > 0) {
      toast.warning(`CCTP générés : ${generated} nouveaux${skipped > 0 ? `, ${skipped} conservés` : ''}, ${errors} échec${errors > 1 ? 's' : ''}`,
        { description: causeEchec, duration: 12000 });
    } else {
      toast.success(`CCTP générés : ${generated} nouveaux${skipped > 0 ? `, ${skipped} conservés` : ''}`, { duration: 7000 });
    }
  };

  // Get active DPGF lot and CCTP lot
  const activeDPGFLot = dpgfLots.find(l => l.id === activeLotId);
  const activeCCTPLot = cctpData.find(l => l.lot_id === activeLotId);

  // Completion per lot
  const getCompletion = (lotId) => {
    const cctp = cctpData.find(l => l.lot_id === lotId);
    if (!cctp || cctp.postes.length === 0) return 0;
    const filled = cctp.postes.filter(p => p.descriptif && p.descriptif.length > 10).length;
    return Math.round((filled / cctp.postes.length) * 100);
  };

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  if (dpgfLots.length === 0) {
    return (
      <div data-testid="cctp-editor">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" className="rounded-none gap-2 text-[#6B6B6B]" onClick={onBack} data-testid="cctp-back">
            <ArrowLeft size={18} /> Retour
          </Button>
          <h2 className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>CCTP — {chantierName}</h2>
        </div>
        <div className="card-kroquis text-center py-16">
          <p className="text-[#6B6B6B] mb-4">Générez d'abord votre DPGF pour créer la structure du CCTP</p>
          <Button onClick={onBack} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2">
            <ArrowLeft size={16} /> Aller à la DPGF
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cctp-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="rounded-none gap-2 text-[#6B6B6B] hover:text-[#2C2C2C]" onClick={onBack} data-testid="cctp-back">
            <ArrowLeft size={18} /> Retour
          </Button>
          <div>
            <h2 className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              CCTP — {chantierName}
            </h2>
            <p className="text-xs text-[#6B6B6B]">Cahier des Clauses Techniques Particulières</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-none gap-1 text-xs h-8 border-[#6A1B9A] text-[#6A1B9A] hover:bg-[#F3E5F5]"
            onClick={generateAllCCTP}
            disabled={!!bulkGen || dpgfLots.length === 0 || !canEdit}
            title={editTitle}
            data-testid="cctp-generate-all"
          >
            {bulkGen ? <Spinner size={14} className="animate-spin" /> : <Robot size={14} weight="bold" />}
            Générer tous les CCTP depuis la DPGF
          </Button>
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/cctp/export-pdf`, '_blank')} data-testid="cctp-export-pdf">
            <FilePdf size={14} /> PDF
          </Button>
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/cctp/export-docx`, '_blank')} data-testid="cctp-export-docx">
            <FileDoc size={14} /> DOCX
          </Button>
          {onNavigateDPGF && (
            <Button variant="outline" className="rounded-none gap-1 text-xs h-8 border-[#1E3A5F] text-[#1E3A5F]" onClick={onNavigateDPGF} data-testid="cctp-nav-dpgf">
              <Rows size={14} /> Voir la DPGF
            </Button>
          )}
        </div>
      </div>

      {/* Sprint D #8 — Bannière non-bloquante (l'utilisateur peut naviguer ailleurs) */}
      {bulkGen && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border border-[#6A1B9A] shadow-xl w-[340px]" data-testid="cctp-bulk-overlay">
          <div className="bg-[#6A1B9A] text-white px-3 py-1.5 flex items-center gap-2">
            <Spinner size={14} className="animate-spin" />
            <h3 className="text-xs font-bold flex-1 uppercase tracking-wide">Génération CCTP en cours</h3>
            <span className="text-[10px] text-white/80">{bulkGen.lotIdx}/{bulkGen.lotTotal} lots</span>
          </div>
          <div className="px-3 py-2">
            <p className="text-xs text-[#2C2C2C] truncate">
              <span className="text-[#6A1B9A] font-semibold">Lot {bulkGen.lotIdx}/{bulkGen.lotTotal}</span> — {bulkGen.lotNom}
            </p>
            <p className="text-[10px] text-[#6B6B6B] mb-2">
              Poste {bulkGen.posteIdx}/{bulkGen.posteTotal}
            </p>
            <div className="h-1.5 bg-[#E0E0DB] overflow-hidden">
              <div
                className="h-full bg-[#6A1B9A] transition-all"
                style={{ width: `${((bulkGen.lotIdx - 1 + (bulkGen.posteTotal > 0 ? bulkGen.posteIdx / bulkGen.posteTotal : 1)) / bulkGen.lotTotal) * 100}%` }}
              />
            </div>
            <p className="text-[10px] text-[#9A9A9A] mt-2 italic">Vous pouvez naviguer · descriptifs déjà saisis préservés</p>
          </div>
        </div>
      )}

      {/* Two columns */}
      <div className="flex gap-0 border border-[#E0E0DB]">
        {/* Left: Lot navigation (30%) */}
        <div className="w-[280px] border-r border-[#E0E0DB] bg-[#FAFAF8] flex-shrink-0">
          <div className="p-3 border-b border-[#E0E0DB]">
            <p className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Lots DPGF</p>
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {dpgfLots.map(lot => {
              const pct = getCompletion(lot.id);
              const isActive = activeLotId === lot.id;
              return (
                <button
                  key={lot.id}
                  onClick={() => setActiveLotId(lot.id)}
                  className={`w-full text-left px-4 py-3 border-b border-[#F0F0ED] transition-colors ${isActive ? 'bg-white border-l-2 border-l-[#C75B39]' : 'hover:bg-white'}`}
                  data-testid={`cctp-lot-nav-${lot.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isActive ? 'text-[#C75B39]' : 'text-[#2C2C2C]'}`}>{lot.nom}</span>
                    {pct === 100 ? (
                      <CheckCircle size={16} className="text-[#2D6A4F]" weight="fill" />
                    ) : (
                      <span className="text-[10px] font-bold text-[#6B6B6B]">{pct}%</span>
                    )}
                  </div>
                  {/* Progress bar */}
                  <div className="h-1 bg-[#E0E0DB] overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${pct === 100 ? 'bg-[#2D6A4F]' : 'bg-[#C75B39]'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[10px] text-[#9A9A9A] mt-1">{lot.postes?.length || 0} postes</p>
                  {lotsConfies.delegationDe(lot.id) && (
                    <p className="text-[10px] text-[#1E3A5F] mt-0.5" data-testid={`cctp-lot-confie-${lot.id}`}>
                      {lotsConfies.delegationDe(lot.id).user_nom} · {lotsConfies.delegationDe(lot.id).statut_libelle}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Editor (70%) */}
        <div className="flex-1 bg-white overflow-y-auto max-h-[650px]">
          {activeDPGFLot ? (
            <div className="p-6">
              {/* RP44-46 — lot confié : à qui, statut, signaler / valider */}
              <div className="-mx-6 -mt-6 mb-4">
                <LotConfieBar chantierId={chantierId} lotId={activeLotId} lotNom={activeDPGFLot.nom} lotsConfies={lotsConfies} />
              </div>
              <h3 className="text-lg font-semibold text-[#2C2C2C] mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
                {activeDPGFLot.nom}
              </h3>

              {/* Generalites */}
              <div className="mb-6">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2 block">Généralités du lot</Label>
                <Textarea
                  value={activeCCTPLot?.generalites || ''}
                  onChange={e => updateGeneralites(activeLotId, e.target.value)}
                  readOnly={!canEditLot(activeLotId)}
                  title={canEditLot(activeLotId) ? undefined : editTitle}
                  data-testid="cctp-generalites-textarea"
                  rows={4}
                  className="rounded-none text-sm border-[#E0E0DB] focus:border-[#C75B39]"
                  placeholder="Généralités techniques du lot..."
                />
              </div>

              {/* Postes */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3 block">Postes ({activeDPGFLot.postes?.length || 0})</Label>
                {activeDPGFLot.postes?.map(dpgfPoste => {
                  const cctpPoste = activeCCTPLot?.postes?.find(p => p.poste_id === dpgfPoste.id) || { poste_id: dpgfPoste.id, descriptif: '' };
                  return (
                    <PosteEditor
                      key={dpgfPoste.id}
                      poste={cctpPoste}
                      dpgfPoste={dpgfPoste}
                      lotNom={activeDPGFLot.nom}
                      chantierId={chantierId}
                      canEdit={canEditLot(activeLotId)}
                      canIA={!!perms?.can_modify_cctp}
                      onUpdate={text => updatePosteDescriptif(activeLotId, dpgfPoste.id, text)}
                      onSaveBiblio={saveToBibliotheque}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-[#6B6B6B]">
              Sélectionnez un lot à gauche
            </div>
          )}
        </div>
      </div>

      {/* Auto-save indicator */}
      <div className="mt-4 flex items-center gap-2 text-xs text-[#9A9A9A]" data-testid="cctp-save-status">
        <FloppyDisk size={14} /> {canEdit
          ? 'Sauvegarde automatique activée (lot par lot)'
          : porteeCctp.mode !== 'aucun'
            ? 'Sauvegarde automatique de vos lots · les autres lots restent en lecture'
            : 'Lecture seule — demandez à l\'architecte de vous confier un lot'}
      </div>
    </div>
  );
}
